import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Typography,
    TextField,
    Button,
    CircularProgress
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from './SnackbarContext';
import config from '../config';
import axios from 'axios';

const useStyles = makeStyles(() => ({
    rootContainer: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'white',
        padding: '16px',
        overflow: 'hidden',
        fontFamily: 'Nunito, Poppins, Sansation, sans-serif',
    },
    verificationCard: {
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'white',
        overflow: 'hidden',
        borderRadius: 30,
        minHeight: 600,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        '@media (max-width: 768px)': {
            borderRadius: 0,
            boxShadow: 'none',
            minHeight: '100vh',
        },
    },
    backButton: {
        position: 'absolute',
        top: 20,
        left: 20,
        background: 'none',
        border: 'none',
        color: '#C084FC',
        fontSize: 16,
        fontFamily: 'Nunito',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        '&:hover': {
            color: '#724F96',
        },
    },
    title: {
        color: '#C084FC',
        fontSize: 30.26,
        fontFamily: 'Poppins',
        fontWeight: '700',
        lineHeight: '30.75px',
        wordWrap: 'break-word',
        marginBottom: '20px',
    },
    subtitle: {
        color: '#A1A1A1',
        fontSize: 16,
        fontFamily: 'Nunito',
        fontWeight: '500',
        lineHeight: '24px',
        wordWrap: 'break-word',
        marginBottom: '30px',
    },
    otpContainer: {
        display: 'flex',
        gap: 8,
        justifyContent: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        maxWidth: '100%',
        padding: '0 10px',
    },
    otpBox: {
        width: 50,
        height: 50,
        borderRadius: 15,
        border: '1.60px #DDDDDD solid',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        fontFamily: 'Poppins',
        fontWeight: '700',
        color: '#757575',
        background: 'white',
        cursor: 'text',
        transition: 'all 0.3s ease',
        position: 'relative',
        flexShrink: 0,
        '&.active': {
            border: '1.80px #C084FC solid',
        },
        '&.filled': {
            border: '1.80px #C084FC solid',
        },
    },
    otpInput: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 1,
        cursor: 'text',
        textAlign: 'center',
        fontSize: 18,
        fontFamily: 'Poppins',
        fontWeight: '700',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        zIndex: 10,
        color: '#757575',
        caretColor: '#C084FC',
        '&:focus': {
            outline: 'none',
        },
    },
    resendOtp: {
        color: '#009DFF',
        fontSize: 16,
        fontFamily: 'Poppins',
        fontWeight: '500',
        wordWrap: 'break-word',
        cursor: 'pointer',
        textAlign: 'center',
        marginBottom: '20px',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    mainContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 37px',
        paddingTop: '80px', // Space for back button
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        marginTop: '20px',
    },
    verifyButton: {
        width: '100%',
        height: 51,
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
        borderRadius: 10.40,
        border: 'none',
        color: 'white',
        fontSize: 18,
        fontFamily: 'Poppins',
        fontWeight: '700',
        cursor: 'pointer',
        '&:hover': {
            background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
        },
        '&:disabled': {
            background: '#E0E0E0',
            color: '#9E9E9E',
        },
    },
    errorText: {
        color: '#f44336',
        fontSize: 14,
        textAlign: 'center',
        marginTop: 8,
        fontFamily: 'Nunito',
    },
    successText: {
        color: '#4caf50',
        fontSize: 14,
        textAlign: 'center',
        marginTop: 8,
        fontFamily: 'Nunito',
    },
    loadingSpinner: {
        color: 'white',
    },
}));

const EmailVerification = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [activeOtpIndex, setActiveOtpIndex] = useState(0);
    const [otpSent, setOtpSent] = useState(false);

    const { propertyData, legalData, hotelId, email } = location.state || {};
    const otpRefs = [React.useRef(), React.useRef(), React.useRef(), React.useRef(), React.useRef(), React.useRef()];

    useEffect(() => {
        if (!email) {
            navigate('/property-information');
        }
        // Auto-send OTP when component mounts
        handleSendOtp();
    }, []);

    // Auto-focus first OTP input when component mounts
    useEffect(() => {
        setActiveOtpIndex(0);
        setTimeout(() => {
            if (otpRefs[0].current) {
                otpRefs[0].current.focus();
            }
        }, 100);
    }, []);

    const handleOtpChange = (index, value) => {
        // Only allow numeric input
        const numericValue = value.replace(/[^0-9]/g, '');
        
        if (numericValue.length <= 1) {
            const newOtp = [...otp];
            newOtp[index] = numericValue;
            setOtp(newOtp);
            
            // Move to next input if value is entered
            if (numericValue && index < 5) {
                setActiveOtpIndex(index + 1);
                setTimeout(() => {
                    if (otpRefs[index + 1].current) {
                        otpRefs[index + 1].current.focus();
                    }
                }, 10);
            }
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            const newOtp = [...otp];
            
            if (newOtp[index]) {
                newOtp[index] = '';
                setOtp(newOtp);
                setActiveOtpIndex(index);
                setTimeout(() => {
                    if (otpRefs[index].current) {
                        otpRefs[index].current.focus();
                        otpRefs[index].current.setSelectionRange(0, 0);
                    }
                }, 10);
            } else if (index > 0) {
                newOtp[index - 1] = '';
                setOtp(newOtp);
                setActiveOtpIndex(index - 1);
                setTimeout(() => {
                    if (otpRefs[index - 1].current) {
                        otpRefs[index - 1].current.focus();
                        otpRefs[index - 1].current.setSelectionRange(0, 0);
                    }
                }, 10);
            }
        } else if (e.key === 'Enter') {
            // Auto-submit when Enter is pressed and all fields are filled
            const otpString = otp.join('');
            if (otpString.length === 6) {
                handleVerifyOtp(e);
            }
        }
    };

    const handleOtpClick = (index) => {
        setActiveOtpIndex(index);
        if (otpRefs[index].current) {
            otpRefs[index].current.focus();
            otpRefs[index].current.setSelectionRange(0, 0);
        }
    };

    const handleOtpFocus = (index) => {
        setActiveOtpIndex(index);
        if (otpRefs[index].current) {
            otpRefs[index].current.setSelectionRange(0, 0);
        }
    };

    const handleSendOtp = async () => {
        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await axios.post(`${config.API_BASE_URL}/hotels/verify-email`, {
                hotel_id: hotelId,
                email: email
            });

            if (response.data) {
                setOtpSent(true);
                setSuccess('Verification code sent to your email!');
                showSnackbar('Verification code sent successfully!', 'success');
            }
        } catch (error) {
            console.error('Error sending verification email:', error);
            setError('Failed to send verification code');
            showSnackbar('Failed to send verification code', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const otpString = otp.join('');
        if (otpString.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(`${config.API_BASE_URL}/hotels/verify-otp`, {
                hotel_id: hotelId,
                email: email,
                otp: otpString
            });

            if (response.data) {
                setSuccess('Email verified successfully!');
                showSnackbar('Email verified successfully!', 'success');
                
                // Navigate to manager assignment page
                setTimeout(() => {
                    navigate('/manager-assignment', { 
                        state: { 
                            propertyData,
                            legalData,
                            hotelId,
                            email
                        } 
                    });
                }, 1500);
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setError(error.response?.data?.error || 'Invalid verification code');
            showSnackbar('Invalid verification code', 'error');
            // Clear OTP inputs when verification fails to allow re-entry
            setOtp(['', '', '', '', '', '']);
            setActiveOtpIndex(0);
            // Focus the first input for easy re-entry
            setTimeout(() => {
                if (otpRefs[0].current) {
                    otpRefs[0].current.focus();
                }
            }, 100);
        } finally {
            setIsLoading(false);
        }
    };


    const handleBack = () => {
        navigate('/company-legal-details');
    };

    return (
        <div className={classes.rootContainer}>
            <div className={classes.verificationCard}>
                {/* Back Button */}
                <button className={classes.backButton} onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-left"><path d="m12 19-7-7 7-7"/><path d="m19 12H5"/></svg>
                    Back
                </button>

                {/* Main Content */}
                <div className={classes.mainContent}>
                    {/* Title */}
                    <div className={classes.title}>
                        Email Verification
                    </div>

                    {/* Subtitle */}
                    <div className={classes.subtitle}>
                        We have sent a verification code to {email}. Please enter the code below.
                    </div>

                    {/* OTP Input Boxes */}
                    <div className={classes.otpContainer}>
                        {[0, 1, 2, 3, 4, 5].map((index) => (
                            <div
                                key={index}
                                className={`${classes.otpBox} ${
                                    activeOtpIndex === index ? 'active' : ''
                                } ${otp[index] ? 'filled' : ''}`}
                                onClick={() => handleOtpClick(index)}
                            >
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className={classes.otpInput}
                                    value={otp[index]}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    onFocus={() => handleOtpFocus(index)}
                                    onClick={(e) => e.stopPropagation()}
                                    maxLength={1}
                                    disabled={isLoading}
                                    ref={otpRefs[index]}
                                    autoComplete="off"
                                />
                            </div>
                        ))}
                    </div>

                    {/* Resend OTP */}
                    <div className={classes.resendOtp} onClick={handleSendOtp}>
                        Resend verification code?
                    </div>

                    {/* Button Container */}
                    <div className={classes.buttonContainer}>
                        {/* Verify Button */}
                        <button
                            className={classes.verifyButton}
                            onClick={handleVerifyOtp}
                            disabled={isLoading || otp.join('').length !== 6}
                        >
                            {isLoading ? (
                                <CircularProgress size={20} className={classes.loadingSpinner} />
                            ) : (
                                'Verify Email'
                            )}
                        </button>

                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{ 
                        position: 'absolute', 
                        bottom: 20, 
                        left: '50%', 
                        transform: 'translateX(-50%)',
                        width: '80%',
                        textAlign: 'center'
                    }}>
                        <Typography className={classes.errorText}>
                            {error}
                        </Typography>
                    </div>
                )}

                {/* Success Message */}
                {success && (
                    <div style={{ 
                        position: 'absolute', 
                        bottom: 20, 
                        left: '50%', 
                        transform: 'translateX(-50%)',
                        width: '80%',
                        textAlign: 'center'
                    }}>
                        <Typography className={classes.successText}>
                            {success}
                        </Typography>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailVerification;
